import React, { useCallback, useEffect, useState } from "react";
import {
  Store,
  BadgeCheck,
  Bell,
  ExternalLink,
  Radar,
  LayoutDashboard,
  Users,
  Tag,
  ClipboardList,
  Bot,
  Facebook,
  Search,
  Settings,
  BrainCircuit,
  MessageSquare,
  FlaskConical,
  RefreshCw,
  FolderSearch,
  Play,
} from "lucide-react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Badge } from "@/components/ui/badge";
import { getAllStoresForAdmin, type StoreWithDiscoverer } from "@/api/adminService";
import { startScrapeJob } from "@/api/ecommerceScrapeService";
import { listGroups, triggerScrape, runReconciliation, type FacebookGroup } from "@/api/fbGroupReviewService";
import { useNavigate } from "@/hooks/useNavigate";
import { deriveSearchKey } from "@/lib/storeSearchKey";
import { toast } from "sonner";

type NavItem = {
  label: string;
  href: string;
  icon: React.ReactNode;
  keywords: string[];
};

const NAV_ITEMS: NavItem[] = [
  {
    label: "Admin Dashboard",
    href: "/admin",
    icon: <LayoutDashboard className="h-4 w-4 shrink-0 text-muted-foreground" />,
    keywords: ["dashboard", "overview", "home", "admin"],
  },
  {
    label: "Stores",
    href: "/admin?tab=stores",
    icon: <Store className="h-4 w-4 shrink-0 text-muted-foreground" />,
    keywords: ["stores", "shops", "products", "draft", "published"],
  },
  {
    label: "Categories",
    href: "/admin?tab=categories",
    icon: <Tag className="h-4 w-4 shrink-0 text-muted-foreground" />,
    keywords: ["categories", "category", "taxonomy"],
  },
  {
    label: "Users",
    href: "/admin?tab=users",
    icon: <Users className="h-4 w-4 shrink-0 text-muted-foreground" />,
    keywords: ["users", "accounts", "admins", "members"],
  },
  {
    label: "Submissions",
    href: "/admin?tab=submissions",
    icon: <ClipboardList className="h-4 w-4 shrink-0 text-muted-foreground" />,
    keywords: ["submissions", "pending", "review", "requests"],
  },
  {
    label: "Scraper Agent",
    href: "/admin?tab=scraper",
    icon: <Bot className="h-4 w-4 shrink-0 text-muted-foreground" />,
    keywords: ["scraper", "ecommerce", "agent", "jobs", "scrape"],
  },
  {
    label: "FB Group Reviews",
    href: "/admin/fb-reviews",
    icon: <Facebook className="h-4 w-4 shrink-0 text-muted-foreground" />,
    keywords: ["facebook", "fb", "groups", "reviews", "social"],
  },
  {
    label: "Discovery",
    href: "/admin/discovery",
    icon: <FolderSearch className="h-4 w-4 shrink-0 text-muted-foreground" />,
    keywords: ["discovery", "campaigns", "search", "find", "discover"],
  },
  {
    label: "Reviews",
    href: "/admin/reviews",
    icon: <ClipboardList className="h-4 w-4 shrink-0 text-muted-foreground" />,
    keywords: ["reviews", "ratings", "feedback", "manage"],
  },
  {
    label: "Claims",
    href: "/admin/claims",
    icon: <BadgeCheck className="h-4 w-4 shrink-0 text-muted-foreground" />,
    keywords: ["claims", "ownership", "merchant", "verify", "pending edits"],
  },
  {
    label: "Shopify Stores",
    href: "/admin/shopify",
    icon: <Store className="h-4 w-4 shrink-0 text-muted-foreground" />,
    keywords: ["shopify", "shops", "link", "install", "relink"],
  },
  {
    label: "Settings",
    href: "/admin/settings",
    icon: <Settings className="h-4 w-4 shrink-0 text-muted-foreground" />,
    keywords: ["settings", "config", "configuration", "app", "preferences"],
  },
  {
    label: "Notifications",
    href: "/admin/notifications",
    icon: <Bell className="h-4 w-4 shrink-0 text-muted-foreground" />,
    keywords: ["notifications", "digest", "quiet hours", "whatsapp", "push", "email", "delivery log"],
  },
  {
    label: "AI Logs",
    href: "/admin/ai-logs",
    icon: <BrainCircuit className="h-4 w-4 shrink-0 text-muted-foreground" />,
    keywords: ["ai", "logs", "operations", "llm", "openrouter", "cost"],
  },
  {
    label: "Chat Monitor",
    href: "/admin/chat",
    icon: <MessageSquare className="h-4 w-4 shrink-0 text-muted-foreground" />,
    keywords: ["chat", "messenger", "conversations", "monitor"],
  },
  {
    label: "Playground",
    href: "/admin/playground",
    icon: <FlaskConical className="h-4 w-4 shrink-0 text-muted-foreground" />,
    keywords: ["playground", "test", "firecrawl", "ollama", "redis", "debug"],
  },
];

function matchesQuery(item: NavItem, q: string): boolean {
  const lower = q.toLowerCase();
  return (
    item.label.toLowerCase().includes(lower) ||
    item.keywords.some((k) => k.includes(lower))
  );
}

function matchesGroupQuery(group: FacebookGroup, q: string): boolean {
  return group.name.toLowerCase().includes(q.toLowerCase());
}

export function AdminCommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [stores, setStores] = useState<StoreWithDiscoverer[]>([]);
  const [fbGroups, setFbGroups] = useState<FacebookGroup[]>([]);
  const [storesLoading, setStoresLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setStores([]);
      return;
    }
    // Load FB groups once when palette opens
    listGroups().then((res) => {
      if (res.ok && res.data) setFbGroups(res.data.filter((g) => g.isActive !== false));
    });
  }, [open]);

  const searchStores = useCallback(async (q: string) => {
    if (!q) {
      setStores([]);
      return;
    }
    setStoresLoading(true);
    const result = await getAllStoresForAdmin({ search: q, limit: 10 });
    if (result.ok && result.data) {
      setStores(result.data.stores);
    } else {
      setStores([]);
    }
    setStoresLoading(false);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (open) {
        const trimmed = query.trim();
        const term = isUrl(trimmed) ? deriveSearchKey(trimmed) : query;
        searchStores(term);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query, open, searchStores]);

  const isUrl = (s: string) => {
    try {
      const u = new URL(s);
      return u.protocol === "http:" || u.protocol === "https:";
    } catch {
      return false;
    }
  };

  const handleScrapeUrl = async () => {
    setOpen(false);
    const url = query.trim();
    toast.loading("Starting scrape job...", { id: "scrape-start" });
    const result = await startScrapeJob({ url });
    if (result.ok) {
      toast.success("Scrape job started", { id: "scrape-start" });
    } else {
      toast.error(result.error || "Failed to start scrape job", { id: "scrape-start" });
    }
  };

  const handleScrapeGroup = async (group: FacebookGroup) => {
    setOpen(false);
    toast.loading(`Scraping ${group.name}...`, { id: "fb-scrape" });
    const result = await triggerScrape(group._id);
    if (result.ok) {
      toast.success(`Scrape run started for ${group.name}`, { id: "fb-scrape" });
    } else {
      toast.error(result.error || "Failed to trigger scrape", { id: "fb-scrape" });
    }
  };

  const handleReconcile = async () => {
    setOpen(false);
    toast.loading("Running reconciliation...", { id: "reconcile" });
    const result = await runReconciliation();
    if (result.ok && result.data) {
      const { reconciledByJobId, reconciledByName } = result.data;
      const total = reconciledByJobId + reconciledByName;
      toast.success(`Reconciled ${total} reviews`, { id: "reconcile" });
    } else {
      toast.error(result.error || "Reconciliation failed", { id: "reconcile" });
    }
  };

  const handleNavSelect = (href: string) => {
    setOpen(false);
    navigate(href);
  };

  const handleStoreSelect = (store: StoreWithDiscoverer) => {
    setOpen(false);
    navigate(`/store/${store.slug}`);
  };

  const urlDetected = isUrl(query.trim());
  const filteredNav = query ? NAV_ITEMS.filter((item) => matchesQuery(item, query)) : NAV_ITEMS;
  const filteredGroups = query
    ? fbGroups.filter((g) => matchesGroupQuery(g, query))
    : fbGroups;

  const showNav = filteredNav.length > 0;
  const showGroups = filteredGroups.length > 0;
  const showStores = stores.length > 0;
  const showExistingMatches = urlDetected && showStores;
  const showActions = urlDetected || !query; // reconcile always visible when empty

  return (
    <CommandDialog open={open} onOpenChange={setOpen} shouldFilter={false}>
      <CommandInput
        placeholder="Search stores, navigate, or trigger agents..."
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        {/* Already-existing store matches for a pasted URL (shown above Scrape) */}
        {showExistingMatches && (
          <CommandGroup heading="Already exists">
            {stores.map((store) => (
              <CommandItem
                key={store._id}
                value={`existing-${store._id}`}
                onSelect={() => handleStoreSelect(store)}
                className="flex items-center gap-3 cursor-pointer"
              >
                {store.logoUrl ? (
                  <img
                    src={store.logoUrl}
                    alt=""
                    className="h-6 w-6 rounded object-cover shrink-0"
                  />
                ) : (
                  <Store className="h-4 w-4 shrink-0 text-muted-foreground" />
                )}
                <span className="flex-1 truncate">{store.name}</span>
                {store.status === "draft" && (
                  <Badge variant="secondary" className="text-xs shrink-0">
                    draft
                  </Badge>
                )}
                <ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground" />
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {/* Actions */}
        {showActions && (
          <>
            {showExistingMatches && <CommandSeparator />}
            <CommandGroup heading="Actions">
            {urlDetected && (
              <CommandItem
                value="scrape-url"
                onSelect={handleScrapeUrl}
                className="flex items-center gap-3 cursor-pointer"
              >
                <Radar className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="flex-1 truncate">
                  Scrape{" "}
                  <span className="font-mono text-xs text-muted-foreground">
                    {query.trim()}
                  </span>
                </span>
              </CommandItem>
            )}
            {!query && (
              <CommandItem
                value="reconcile"
                onSelect={handleReconcile}
                className="flex items-center gap-3 cursor-pointer"
              >
                <RefreshCw className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="flex-1">Run FB Review Reconciliation</span>
              </CommandItem>
            )}
            </CommandGroup>
          </>
        )}

        {/* Navigation */}
        {showNav && (
          <>
            {showActions && <CommandSeparator />}
            <CommandGroup heading="Navigate to">
              {filteredNav.map((item) => (
                <CommandItem
                  key={item.href}
                  value={item.href}
                  onSelect={() => handleNavSelect(item.href)}
                  className="flex items-center gap-3 cursor-pointer"
                >
                  {item.icon}
                  <span className="flex-1">{item.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {/* FB Group Scrape triggers */}
        {showGroups && (
          <>
            {(showActions || showNav) && <CommandSeparator />}
            <CommandGroup heading="Scrape FB Group">
              {filteredGroups.map((group) => (
                <CommandItem
                  key={group._id}
                  value={`scrape-group-${group._id}`}
                  onSelect={() => handleScrapeGroup(group)}
                  className="flex items-center gap-3 cursor-pointer"
                >
                  <Play className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="flex-1 truncate">{group.name}</span>
                  <Search className="h-3 w-3 shrink-0 text-muted-foreground" />
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {/* Live store search */}
        {storesLoading && (
          <div className="py-6 text-center text-sm text-muted-foreground">
            Searching...
          </div>
        )}
        {!storesLoading && !urlDetected && showStores && (
          <>
            {(showActions || showNav || showGroups) && <CommandSeparator />}
            <CommandGroup heading="Stores">
              {stores.map((store) => (
                <CommandItem
                  key={store._id}
                  value={store._id}
                  onSelect={() => handleStoreSelect(store)}
                  className="flex items-center gap-3 cursor-pointer"
                >
                  {store.logoUrl ? (
                    <img
                      src={store.logoUrl}
                      alt=""
                      className="h-6 w-6 rounded object-cover shrink-0"
                    />
                  ) : (
                    <Store className="h-4 w-4 shrink-0 text-muted-foreground" />
                  )}
                  <span className="flex-1 truncate">{store.name}</span>
                  {store.status === "draft" && (
                    <Badge variant="secondary" className="text-xs shrink-0">
                      draft
                    </Badge>
                  )}
                  <ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground" />
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {/* Empty state — only when actively searching and nothing found */}
        {query && !storesLoading && !showStores && !showNav && !showGroups && !urlDetected && (
          <CommandEmpty>No results for "{query}"</CommandEmpty>
        )}
      </CommandList>
    </CommandDialog>
  );
}
